import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../index";
import { getFleetsByCurrentSemanticMap } from "../fleets";
import { getOperatorPickedStation } from "../operatorStation";

const getOperatorMissionState = (state: RootState) =>
  state.operatorData.operatorMissionState;
export const getFleetMissions = (state: RootState) =>
  state.operatorData.fleetMissionsState;
export const getOperatorMissionStatus = createSelector(
  getOperatorMissionState,
  (operatorMissionState) => operatorMissionState?.status
);

// True when the operator's currently-tied mission has reached its final stop
// (passedStops >= totalStops). For Call Vehicle (single-stop uber) missions
// this means the called vehicle has arrived at the operator's station and is
// idle/waiting — it is safe to call another vehicle without blocking on the
// generic "in progress" guard.
export const getIsOperatorMissionArrived = createSelector(
  getOperatorMissionState,
  getFleetMissions,
  (operatorMissionState, fleetMissions) => {
    const queuedId = operatorMissionState?.queuedMissionId;
    if (!queuedId) return false;
    const mission = fleetMissions.find((m) => m.id === queuedId);
    if (!mission) return false;
    const passed = mission.passedStops ?? 0;
    const total = mission.totalStops ?? mission.stops?.length ?? 0;
    return total > 0 && passed >= total;
  }
);
export const getMissionOperatorPickedFleetId = (state: RootState) =>
  state.operatorData.pickedFleetId;

export const getIsPickedFleetHasVehicles = createSelector(
  getMissionOperatorPickedFleetId,
  getFleetsByCurrentSemanticMap,
  (fleetId, fleets) => {
    const pickedFleet = fleets.find((fleet) => fleet.id === fleetId);
    return Boolean(pickedFleet?.vehiclesId.length);
  }
);

export const getMissionOperatorFleetIdForSubscribe = (state: RootState) =>
  state.operatorData.operatorMissionState?.fleetId;

export const getOperatorMissionOrder = createSelector(
  getOperatorMissionState,
  (operatorMissionState) => operatorMissionState?.order
);

export const getOperatorPanelOccupied = createSelector(
  getOperatorMissionState,
  (operatorMissionState) => operatorMissionState?.operatorPanelOccupied
);

// Station-aware variant of `getOperatorPanelOccupied`. The raw flag is a
// single global boolean for "operator has a tied mission in flight"; on its
// own it blocks Call Vehicle even when the operator switches to a different
// station that is unrelated to that mission. Block only when the tied
// mission's stationStopId matches the currently picked Stop UUID. When the
// flag is set but we can't resolve a tied mission (no queuedMissionId, or it
// isn't in fleetMissions), fall back to blocking — that matches the old
// behavior and avoids stomping on whatever the global flag is guarding.
//
// IMPORTANT — this comparison relies on a cloud-core invariant.
// `tiedMission.stationStopId` is BE-derived from the mission's first
// QueuedMissionStop (`stops[0].original_stop_id`), NOT from the operator's
// pick at dispatch — same foot-gun the dispatchOrigins slice was added to
// avoid for the in-progress slider. The check works here only because cloud-
// core's `_mission_matches_user_station_stop` (cloud-core/missions/services.py)
// refuses to tie a mission to `operatorMissionState` unless its first stop
// equals the operator's picked Stop. So in practice the `===` is vacuous
// while a mission is tied, and its real job is the race window between a
// station-pick switch and the WS resubscribe (local state still shows the
// previous station's tied mission for a few hundred ms).
//
// If that BE invariant ever changes — e.g. cloud-core starts tying multi-stop
// missions whose stops[0] is not the operator's pick — this selector silently
// re-enables Call Vehicle on stations the operator is actively busy with.
// The robust fix is to compare against `dispatchOrigins[queuedMissionId]`
// instead, mirroring the slider; deferring that until someone has cycles to
// verify the swap end-to-end.
export const getIsOperatorPanelOccupiedForPickedStation = createSelector(
  getOperatorMissionState,
  getFleetMissions,
  getOperatorPickedStation,
  (operatorMissionState, fleetMissions, pickedStation) => {
    if (!operatorMissionState?.operatorPanelOccupied) return false;
    const queuedId = operatorMissionState?.queuedMissionId;
    if (!queuedId) return true;
    const tiedMission = fleetMissions.find((m) => m.id === queuedId);
    if (!tiedMission) return true;
    if (!pickedStation) return true;
    return tiedMission.stationStopId === pickedStation.id;
  }
);

export const getInProgressFleetMissions = createSelector(
  getFleetMissions,
  (fleetMissions) =>
    fleetMissions.length > 0
      ? fleetMissions.filter(
          (fleetMission) => fleetMission.status === "IN_PROGRESS"
        ).length
      : "..."
);
export const getReleasedStops = (state: RootState): Record<string, number> =>
  state.operatorData.releasedStops;

export const getInQueueFleetMissions = createSelector(
  getFleetMissions,
  (fleetMissions) =>
    fleetMissions.length > 0
      ? fleetMissions.filter(
          (fleetMission) =>
            fleetMission.status === "NEW" ||
            fleetMission.status === "SENT" ||
            fleetMission.status === "RECEIVED"
        ).length
      : "..."
);
